/**
 * What Microsoft Planner gives a task (Aaron, Oct 25, notes 1.3 and 1.5;
 * `docs/activity.md` §8.7, migration 0144): a **checklist**, a **priority**
 * and **categories**. The words, the limits and the shapes both sides read.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------
/**
 * Planner's four, most pressing first. **Medium is the default** and draws no
 * badge — as Planner draws it — so a task nobody gave a priority reads as an
 * ordinary one; it is stored as NULL (0144), which is also what every task
 * made before priorities existed holds.
 */
export const TASK_PRIORITIES = ['urgent', 'important', 'medium', 'low'];
export const DEFAULT_TASK_PRIORITY = 'medium';
export const TASK_PRIORITY_LABELS = {
    urgent: 'Urgent',
    important: 'Important',
    medium: 'Medium',
    low: 'Low',
};
/**
 * The list engine's sortable keys (§8.0's trick for `dueGroup`): ascending
 * is most pressing first, and a group header reads the label.
 */
export const TASK_PRIORITY_KEYS = {
    urgent: '1-urgent',
    important: '2-important',
    medium: '3-medium',
    low: '4-low',
};
/** `3-medium` → *Medium*; anything else as it came. */
export function priorityKeyLabel(key) {
    const found = TASK_PRIORITIES.find((priority) => TASK_PRIORITY_KEYS[priority] === key);
    return found ? TASK_PRIORITY_LABELS[found] : key;
}
/** `1-urgent` → `urgent`; anything else is Medium. */
export function priorityOfKey(key) {
    return TASK_PRIORITIES.find((priority) => TASK_PRIORITY_KEYS[priority] === key) ?? DEFAULT_TASK_PRIORITY;
}
/** What the column stores: NULL for Medium (0144). */
export const storedPriority = (priority) => priority === 'medium' ? null : priority;
/** What a stored value reads as. */
export const priorityOf = (stored) => stored && TASK_PRIORITIES.includes(stored) ? stored : DEFAULT_TASK_PRIORITY;
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);
/** How many one set (a list's, or the company's) may keep, and how many one task may wear. */
export const MAX_TASK_CATEGORIES = 50;
export const MAX_CATEGORIES_ON_A_TASK = 25;
export const taskCategorySchema = z.object({
    name: z.string().max(80),
    colour: z.string().max(20),
});
export const taskCategoryPatchSchema = z.object({
    name: z.string().max(80).optional(),
    colour: z.string().max(20).optional(),
});
/** The categories a task wears — the whole set, never a delta. */
export const taskCategoryIdsSchema = z.array(z.string().uuid()).max(MAX_CATEGORIES_ON_A_TASK);
// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------
/** The longest item (0144's CHECK says the same) and the most one task may hold. */
export const CHECKLIST_ITEM_MAX_CHARS = 200;
export const MAX_CHECKLIST_ITEMS = 50;
/** *2/5* — the progress Planner prints on a card. */
export const checklistWords = (progress) => `${progress.done}/${progress.total}`;
const itemTitle = z.string().trim()
    .min(1, 'Write the item first.')
    .max(CHECKLIST_ITEM_MAX_CHARS, `An item can be up to ${CHECKLIST_ITEM_MAX_CHARS} characters.`);
export const checklistAddSchema = z.object({
    title: itemTitle,
    assigneeId: z.string().uuid().nullish(),
});
export const checklistPatchSchema = z.object({
    title: itemTitle.optional(),
    done: z.boolean().optional(),
    assigneeId: z.string().uuid().nullish(),
});
/** The whole order, after a drag or a move up / down. */
export const checklistOrderSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(MAX_CHECKLIST_ITEMS),
});
//# sourceMappingURL=task-planner.js.map